/**
 * LikeButton — Botón de Me Gusta anónimo para creadoras.
 *
 * - Cualquier usuario con sesión puede dar/quitar like (toggle).
 * - Solo se muestra el contador total; nunca quién ha likeado.
 * - Actualización optimista: la UI responde al instante.
 * - Sin sesión: muestra el contador pero el clic pide iniciar sesión.
 */
import { useState, useEffect, useCallback } from "react";
import { Heart } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "../lib/api";
/**
 * @param {string}  creatorId     - user_id de la creadora
 * @param {number}  initialCount  - contador inicial (del listado)
 * @param {boolean} initialLiked  - estado inicial (del listado, si se precargó)
 * @param {"sm"|"md"|"lg"} size   - tamaño del botón
 * @param {boolean} showCount     - mostrar número junto al corazón
 * @param {string}  className     - clases extra
 */
export function LikeButton({
  creatorId,
  initialCount = 0,
  initialLiked = false,
  size = "md",
  showCount = true,
  className = "",
}) {
  const { user } = useAuth();
  const [liked, setLiked] = useState(initialLiked);
  const [count, setCount] = useState(initialCount);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  // Sincroniza el estado cuando cambian las props (p.ej. al navegar entre perfiles)
  useEffect(() => {
    setCount(initialCount);
    setLiked(initialLiked);
    setFetched(false);
  }, [creatorId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Carga el estado real desde el backend al montar
  useEffect(() => {
    if (!creatorId || fetched) return;
    setFetched(true);
    axios
      .get(`${API}/creators/${creatorId}/like-status`, { withCredentials: true })
      .then((res) => {
        setLiked(res.data.liked);
        setCount(res.data.count);
      })
      .catch(() => {}); // Sin sesión → solo se usa el contador inicial
  }, [creatorId, fetched]);

  const handleToggle = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (!user) {
        toast.info("Inicia sesión para dar me gusta");
        return;
      }
      if (loading) return;

      // Actualización optimista
      const nextLiked = !liked;
      const nextCount = nextLiked ? count + 1 : Math.max(0, count - 1);
      setLiked(nextLiked);
      setCount(nextCount);
      setLoading(true);

      try {
        const res = await axios.post(
          `${API}/creators/${creatorId}/like`,
          {},
          { withCredentials: true }
        );
        setLiked(res.data.liked);
        setCount(res.data.count);
      } catch (err) {
        // Revertir en caso de error
        setLiked(liked);
        setCount(count);
        toast.error(err.response?.data?.detail || "Error al procesar el me gusta");
      } finally {
        setLoading(false);
      }
    },
    [creatorId, liked, count, loading, user]
  );

  const sizes = {
    sm: { icon: "w-3 h-3",   text: "text-[11px]", btn: "gap-1 px-2 py-0.5 rounded-full"  },
    md: { icon: "w-3.5 h-3.5", text: "text-xs",   btn: "gap-1 px-2.5 py-1 rounded-full"  },
    lg: { icon: "w-4 h-4",   text: "text-sm",     btn: "gap-1.5 px-3 py-1.5 rounded-full" },
  };
  const s = sizes[size] || sizes.md;

  return (
    <button
      type="button"
      onClick={handleToggle}
      disabled={loading}
      data-testid={`like-btn-${creatorId}`}
      aria-label={liked ? "Quitar me gusta" : "Dar me gusta"}
      aria-pressed={liked}
      className={`inline-flex items-center font-heading font-semibold transition-all duration-200 select-none
        ${s.btn}
        ${liked
          ? "bg-neon-pink/15 border border-neon-pink/50 text-neon-pink hover:bg-neon-pink/25"
          : "bg-zinc-800/60 border border-zinc-700/60 text-zinc-400 hover:border-neon-pink/40 hover:text-neon-pink"
        }
        ${loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer active:scale-95"}
        ${className}`}
    >
      <Heart
        className={`${s.icon} transition-all duration-200 ${liked ? "scale-110 drop-shadow-[0_0_4px_rgba(255,0,127,0.6)]" : ""}`}
        fill={liked ? "currentColor" : "none"}
        strokeWidth={2}
      />
      {showCount && (
        <span
          className={`${s.text} tabular-nums`}
          data-testid={`like-count-${creatorId}`}
        >
          {count}
        </span>
      )}
    </button>
  );
}
